import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardMedia, Typography, Grid, Container, IconButton, Box, Button, CardActions, TextField, InputAdornment, CircularProgress, Snackbar, Alert } from '@mui/material';
import { ArrowBack, Search } from '@mui/icons-material';
import ProductEditModal from './ProductEditModal';
import Loader from './Loader';
import { db } from './Dexie';
import { useLiveQuery } from 'dexie-react-hooks';
import genericProduct from '../static/genericProduct.png';
import { log } from '../services/loggingService';
import { syncProducts } from '../services/productService';

function TechyShopProduct() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const hasCheckedRef = useRef(false);

  const handleToastClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setToast({ ...toast, open: false });
  };

  // Usa useLiveQuery per ottenere i prodotti in tempo reale dal DB
  const products = useLiveQuery(() => db.products.toArray(), []);

  // Effetto per caricare i prodotti se il DB è vuoto o obsoleto
  useEffect(() => {
    // Evita la doppia esecuzione (es. StrictMode o remount rapidi)
    if (hasCheckedRef.current || products === undefined) return;
    hasCheckedRef.current = true;

    const checkAndLoadProducts = async () => {
      const productCount = products.length;
      const lastUpdate = await db.genericTable.get({ table: 'product', field: 'lastupdateproduct' });
      
      const now = new Date();
      let needsUpdate = false;

      if (productCount === 0) {
        log("Nessun prodotto in DB, avvio download...", 'INFO');
        needsUpdate = true;
      } else if (lastUpdate && lastUpdate.date) {
        const lastDate = new Date(lastUpdate.date);
        const diffInMs = now - lastDate;
        const diffInDays = diffInMs / (1000 * 60 * 60 * 24);
        
        if (diffInDays >= 1) {
          log(`Sono passati ${diffInDays.toFixed(2)} giorni dall'ultimo aggiornamento, avvio download...`, 'INFO');
          needsUpdate = true;
        } else {
          log(`Ultimo aggiornamento effettuato meno di un giorno fa (${diffInDays.toFixed(2)} giorni fa).`, 'INFO');
        }
      } else {
        log("Data ultimo aggiornamento non trovata, avvio download per sicurezza...", 'WARNING');
        needsUpdate = true;
      }
      
      if (needsUpdate) {
        setToast({ open: true, message: "Verifica nuovi prodotti in corso...", severity: 'info' });
        const result = await syncProducts(false);
        setToast({ 
          open: true, 
          message: result.message, 
          severity: result.success ? 'success' : 'error' 
        });
      }
    };

    checkAndLoadProducts();
  }, [products]); // Esegue quando products è inizialmente caricato da Dexie

  // Memorizza i prodotti filtrati per evitare ricalcoli costosi
  const filteredProducts = useMemo(() => {
    if (!products) return [];
    
    const lowercasedQuery = searchQuery.toLowerCase();
    return products.filter(product => {
      const id = product.id ? product.id.toString() : '';
      const nome = product.nome || '';
      const descrizioneLunga = product.descrizione_lunga || '';
      const descrizioneBreve = product.descrizione_breve || '';
      const sku = product.sku || '';
      const slug = product.slug || '';
      
      return id.toLowerCase().includes(lowercasedQuery) ||
             nome.toString().toLowerCase().includes(lowercasedQuery) ||
             descrizioneLunga.toString().toLowerCase().includes(lowercasedQuery) ||
             descrizioneBreve.toString().toLowerCase().includes(lowercasedQuery) ||
             sku.toString().toLowerCase().includes(lowercasedQuery) ||
             slug.toString().toLowerCase().includes(lowercasedQuery);
    });
  }, [searchQuery, products]);

  const handleEditClick = (product) => {
    setSelectedProduct(product);
    setIsModalOpen(true);
  };

  const handleCreateClick = () => {
    setSelectedProduct({
      nome: '',
      descrizione_breve: '',
      prezzo: '',
      imageUrl: '',
    });
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedProduct(null);
  };

  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async (productToSave) => {
    setIsSaving(true);
    let success = false;
    if (productToSave.id) {
      // Modifica un prodotto esistente
      try {
        await db.products.put(productToSave);
        success = true;
        setToast({ open: true, message: 'Prodotto aggiornato con successo!', severity: 'success' });
      } catch (error) {
        log("Failed to update product in Dexie:", 'ERROR', error);
        setToast({ open: true, message: "Errore durante l'aggiornamento del prodotto.", severity: 'error' });
      }
    } else {
      // Aggiunge un nuovo prodotto
      try {
        const response = await fetch('https://deadapi.techysumo.it/techyshop/addProdotto', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': process.env.REACT_APP_API_KEY,
          },
          body: JSON.stringify(productToSave),
        });

        const result = await response.json();

        if (result.result) {
          const newId = result.details?.post || result.details?.id || result.details || Date.now();
          const newProduct = {
            ...productToSave,
            id: newId
          };
          // Se la chiamata API ha successo, salva in Dexie
          await db.products.add(newProduct);
          success = true;
          setToast({ open: true, message: 'Prodotto creato con successo!', severity: 'success' });
        }
        else
        {
          throw new Error(result.text || "Errore sconosciuto dall'API.");
        }

      } catch (error) {
        log("Failed to save new product:", 'ERROR', error);
        setToast({ open: true, message: `Errore durante la creazione del nuovo prodotto: ${error.message}`, severity: 'error' });
      }
    }
    setIsSaving(false);
    if (success) {
      handleModalClose();
    }
  };

  if (!products) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Loader />
        <Typography sx={{ mt: 2 }}>Caricamento prodotti...</Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <IconButton onClick={() => navigate('/techyshop')} aria-label="back">
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" component="h1" sx={{ ml: 2, flexGrow: 1 }}>
          Catalogo Prodotti
        </Typography>
        <Button variant="contained" color="primary" onClick={handleCreateClick}>
          Crea Nuovo Prodotto
        </Button>
      </Box>

      <Box sx={{ mb: 4 }}>
        <TextField
          fullWidth
          variant="outlined"
          label="Cerca per nome o descrizione"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Grid container spacing={4}>
        {filteredProducts.map((product) => (
          <Grid item key={product.id} xs={12} sm={6} md={4}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardMedia component="img" height="200" image={genericProduct} alt={product.nome} />
              <CardContent sx={{ flexGrow: 1 }}>
                <Typography>[ {product.id} ]</Typography>
                <Typography gutterBottom variant="h5" component="h2">{product.nome}</Typography>
                <Typography>{product.descrizione_breve}</Typography>
                <Typography variant="h6" color="primary" sx={{ mt: 2 }}>{product.prezzo}</Typography>
              </CardContent>
              <CardActions sx={{ justifyContent: 'center', p: 1 }}>
                <Button size="small" variant="contained" onClick={() => handleEditClick(product)}>
                  Modifica il prodotto
                </Button>
                <Button size="small" variant="contained">
                  Abilita/disabilita
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
        {filteredProducts.length === 0 && (
          <Grid item xs={12}>
            <Typography variant="h6" align="center" sx={{ mt: 4 }}>
              Nessun prodotto trovato.
            </Typography>
          </Grid>
        )}
      </Grid>
      
      <ProductEditModal
        open={isModalOpen}
        onClose={handleModalClose}
        product={selectedProduct}
        onSave={handleSave}
        isSaving={isSaving}
        setToast={setToast}
      />

      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={handleToastClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleToastClose} severity={toast.severity} sx={{ width: '100%' }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default TechyShopProduct;
